import { useState, useEffect } from "react"
import PropTypes from "prop-types"

import { getUserApplets } from "shared/lib/graphql"
import classnames from "classnames"
import EmptyStateRecommendations from "./empty_state_recommendations"
import Search from "./search"
import AppletCard from "./applet_card"
import Upsell from "./upsell"
import Spinner from "shared/components/spinner"
import SearchIndex from "shared/lib/search_index"
import { sortByCreatedAt } from "../shared/utils"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"
import EmptyStateCta from "shared/components/empty_state/index.tsx"
import "./my_applets.scss"

export default function MyApplets({
  archivePath,
  quota,
  userLogin,
  createUrl,
}) {
  const [applets, setApplets] = useState(null)
  const [publishedApplets, setPublishedApplets] = useState(null)
  const [filterApplets, setAppletsFilter] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [searchResults, setSearchResults] = useState(null)
  const [appletsIndex] = useState(new SearchIndex())

  useEffect(() => {
    if (applets && filterApplets == "all" && searchTerm) {
      setSearchResults(
        applets.filter(applet =>
          appletsIndex.search(searchTerm).includes(applet.id)
        )
      )
    } else if (applets && filterApplets == "published_by_me" && searchTerm) {
      setSearchResults(
        publishedApplets.filter(applet =>
          appletsIndex.search(searchTerm).includes(applet.id)
        )
      )
    } else if (applets && filterApplets == "all") {
      setSearchResults(applets)
    } else if (applets && filterApplets == "published_by_me") {
      setSearchResults(publishedApplets)
    }

    // Resize window to fix positioning issues
    window.dispatchEvent(new Event("resize"))
  }, [applets, searchTerm, filterApplets])

  useEffect(() => {
    getUserApplets(userLogin)
      .then(({ live_applets, applets: personalApplets }) => {
        const sortedLiveApplets = sortByCreatedAt([
          ...(live_applets || [])
            .map(la => la.applet.normalized_applet)
            .filter(ma => !(ma.author === userLogin && ma.archived)),
        ])
        const activeLive = sortedLiveApplets.filter(la =>
          Object.is(la.status, "enabled_for_user")
        )
        const inactiveLive = sortedLiveApplets.filter(
          la => !Object.is(la.status, "enabled_for_user")
        )

        const liveAppletIds = new Set(sortedLiveApplets.map(sa => sa.id))

        const createdApplets = (personalApplets || [])
          .map(a => a.normalized_applet)
          .filter(ma => !ma.archived)
          .filter(ma => !liveAppletIds.has(ma.id))
        const activeCreatedApplets = sortByCreatedAt(createdApplets).map(
          applet => {
            return {
              ...applet,
              status: "disabled_for_user", // hack to show connect button as disabled for applets without live applet
            }
          }
        )

        const connections = [
          ...activeLive,
          ...inactiveLive,
          ...activeCreatedApplets,
        ].map(c => ({
          ...c,
          service_names: c.channels.map(ch => ch.name),
        }))
        window.App.pageViewData = window.App.pageViewData || {}
        window.App.pageViewData.applet_ids = connections.map(c => c.id)

        setApplets(connections)
        setPublishedApplets(
          connections?.filter(c => {
            return c.author === userLogin && c.published === true
          })
        )

        connections.forEach(doc => {
          appletsIndex.add(doc.id, doc.name, [
            ...doc.service_names,
            ...(doc.description || "").split(" "),
          ])
        })

        publishedApplets?.forEach(doc => {
          appletsIndex.add(doc.id, doc.name, [
            ...doc.service_names,
            ...(doc.description || "").split(" "),
          ])
        })
      })
      .catch(
        rejectedXHRPromiseLogger(() => {
          setApplets([])
        })
      )
  }, [filterApplets])

  if (searchResults === null) {
    return <Spinner />
  } else if (applets.length === 0) {
    return <EmptyStateRecommendations createPath={createUrl} />
  }

  const counter =
    quota.total > 0
      ? `All (${applets?.length} of ${quota.total})`
      : `All (${applets?.length})`

  const publishingLearnMoreCta = (
    <p>
      <a
        target="_blank"
        className="help_link link-inline"
        href="https://help.ifttt.com/hc/articles/4417802100763"
        rel="noreferrer"
        data-track-ifttt-next-event="click"
        data-track-data={JSON.stringify({
          object_type: "published_applets_help_center",
          location_type: "my_applets",
        })}
      >
        Learn more
      </a>{" "}
      about publishing Applets for anyone to use.
    </p>
  )

  return (
    <>
      <h1>My Applets</h1>

      <Search onSearch={setSearchTerm} value={searchTerm} />

      <Upsell {...quota} extraClass="mobile-version" />

      <div styleName="filters" role="tablist">
        <span
          role="tab"
          aria-label="All Applets"
          styleName={classnames({ active: filterApplets === "all" })}
          onClick={() => setAppletsFilter("all")}
        >
          {counter}
        </span>

        <span
          role="tab"
          aria-label="Applets published by me"
          styleName={classnames({
            active: filterApplets === "published_by_me",
          })}
          onClick={() => setAppletsFilter("published_by_me")}
        >
          {publishedApplets?.length > 0
            ? `Published (${publishedApplets.length})`
            : "Published"}
        </span>

        <span role="tab" aria-label="Applets archive">
          <a
            href={archivePath}
            rel="noreferrer"
            data-track-ifttt-next-event="click"
            data-track-data={JSON.stringify({
              object_type: "view_archive_link",
              location_type: "my_applets",
            })}
          >
            Archive
          </a>
        </span>
        <span role="tab" aria-label="Upgrade to Pro" styleName="upsell_link">
          <Upsell {...quota} />
        </span>
      </div>

      <section className="dashboard-applets">
        <div>
          {filterApplets == "published_by_me" && searchResults.length > 0
            ? publishingLearnMoreCta
            : null}
          {searchResults.length > 0 ? (
            <ul className="web-applet-cards">
              {searchResults.map(applet => (
                <AppletCard key={applet.id} applet={applet} />
              ))}
            </ul>
          ) : filterApplets == "published_by_me" &&
            publishedApplets.length == 0 ? (
            <EmptyStateCta
              heading="No Published Applets."
              copy="Select an Applet you've created or create a new Applet to get started."
              cta="Publish an Applet"
              ctaUrl={createUrl}
              clickTracking={{
                object_type: "publish_your_first_applet",
                location_type: "my_applets",
              }}
            />
          ) : (
            <EmptyStateCta
              heading="No results found..."
              copy="Looks like we couldn't find any Applet matches. Please try again."
            />
          )}
        </div>
      </section>

      <div className="breath-space" />
    </>
  )
}

MyApplets.propTypes = {
  archivePath: PropTypes.string.isRequired,
  quota: PropTypes.shape({
    proPlusSubscriptionUrl: PropTypes.string.isRequired,
    proSubscriptionUrl: PropTypes.string.isRequired,
    tier: PropTypes.string.isRequired,
    total: PropTypes.number.isRequired,
    trialing: PropTypes.bool.isRequired,
  }),
  userLogin: PropTypes.string.isRequired,
  createUrl: PropTypes.string.isRequired,
}
